import { computed, ComputedRef } from 'vue'
import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { ticketKeys } from '@/features/tickets/api/keys'
import {
  assignTicketToUser,
  changeTicketTier,
  checkInTicket,
  getFrontdesk,
  getMyTicketsForEvent,
  getPurchaseReceipt,
  getTicketById,
  getTicketByQRCode,
  getTicketsForEvent,
  getUserTicketsForEvent,
  postCart,
  postCashPayment,
  postCashTierChange,
  postCheckout,
  postStripeTierChangeCheckout,
  refundTicket,
  removeTicket,
  uncheckTicket,
} from '@/features/tickets/api/service'
import { eventKey } from '@/enitites/event/api/keys'
import { api } from '@/plugins/axios'
import { ITicketPurchased } from '@/features/tickets/types'

export const useTicketsForEventQuery = (eventId: number) => {
  return useQuery({
    queryKey: ticketKeys.event(eventId),
    queryFn: eventId ? getTicketsForEvent : skipToken,
  })
}

export const useCartMutation = () => {
  return useMutation({
    mutationFn: postCart,
    scope: {
      id: 'tickets/checkout',
    },
  })
}

export const useCheckoutMutation = () => {
  return useMutation({
    mutationFn: postCheckout,
    scope: {
      id: 'tickets/checkout',
    },
  })
}

export const useMyTicketsForEventQuery = (eventId: number) => {
  return useQuery({
    queryKey: ticketKeys.myTickets(eventId),
    queryFn: eventId ? getMyTicketsForEvent : skipToken,
    select: (data) => data.sort((a, b) => a.id - b.id),
  })
}

export const useUserTicketsForEventQuery = (eventId: number, userId?: number) => {
  return useQuery({
    queryKey: ticketKeys.userTickets(eventId, userId),
    queryFn: eventId && userId ? getUserTicketsForEvent : skipToken,
    select: (data) => data.sort((a, b) => a.id - b.id),
  })
}

export const useAssignTicketToUserMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: assignTicketToUser,
    scope: {
      id: 'tickets/assign',
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketKeys.all,
      })
      queryClient.invalidateQueries({
        queryKey: eventKey.all,
      })
    },
  })
}

export const useFrontdeskQuery = (eventId: number) => {
  return useQuery({
    queryKey: ticketKeys.frontdesk(eventId),
    queryFn: eventId ? getFrontdesk : skipToken,
  })
}

export const useTicketByIdQuery = (ticketId: ComputedRef<number | undefined>) => {
  return useQuery({
    queryKey: computed(() => ticketKeys.ticketById(ticketId)),
    queryFn: (context) => {
      const [, , ticketIdFromKey] = context.queryKey
      if (!ticketIdFromKey) return null
      // @ts-expect-error ignore
      return getTicketById(context)
    },
  })
}

export const useTicketByQRCodeQuery = (qrCode: ComputedRef<string | undefined>) => {
  return useQuery({
    queryKey: computed(() => ticketKeys.ticketByQRCode(qrCode)),
    queryFn: (context) => {
      const [, , , qrCodeFromKey] = context.queryKey
      if (!qrCodeFromKey) return null
      // @ts-expect-error ignore
      return getTicketByQRCode(context)
    },
  })
}

export const useTicketCashPaymentMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: postCashPayment,
    scope: {
      id: 'tickets/cash-payment',
    },
    onSuccess: (_, variables) => {
      const { eventId } = variables
      queryClient.invalidateQueries({
        queryKey: ticketKeys.frontdesk(eventId),
      })
    },
  })
}

export const useCashTierChangeMutation = (eventId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: postCashTierChange,
    scope: {
      id: 'tickets/cash-tier-change',
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketKeys.frontdesk(eventId),
      })
      queryClient.invalidateQueries({
        queryKey: ticketKeys.all,
      })
    },
  })
}

export const useStripeTierChangeMutation = (eventId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: postStripeTierChangeCheckout,
    scope: {
      id: 'tickets/stripe-tier-change',
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketKeys.frontdesk(eventId),
      })
      queryClient.invalidateQueries({
        queryKey: ticketKeys.all,
      })
    },
  })
}

export const useChangeTicketTierMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: changeTicketTier,
    scope: {
      id: 'tickets/change-tier',
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketKeys.all,
      })
    },
  })
}

export const useReceiptQuery = (purchaseId: number) => {
  return useQuery({
    queryKey: ticketKeys.receipt(purchaseId),
    queryFn: getPurchaseReceipt,
  })
}

export const useCheckInTicketMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: checkInTicket,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketKeys.all,
      })
    },
  })
}

export const useRefundTicketMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: refundTicket,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketKeys.all,
      })
    },
  })
}

export const useUncheckTicketMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: uncheckTicket,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketKeys.all,
      })
      queryClient.invalidateQueries({
        queryKey: eventKey.all,
      })
    },
  })
}

export const useRemoveTicketMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: removeTicket,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketKeys.all,
      })
      queryClient.invalidateQueries({
        queryKey: eventKey.all,
      })
    },
  })
}

export const useGetTicketByQRCodeMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (qrCode: string) => {
      const response = await api.get<ITicketPurchased>(`/tickets/qr/${qrCode}`)
      return response.data
    },
    onSuccess: (data) => {
      if (!data) return
      // @ts-expect-error ignore
      queryClient.setQueryData(ticketKeys.ticketById(data.id), data)
    },
    scope: {
      id: 'tickets/get-by-qr-code',
    },
  })
}
